import { useState } from 'react';
import Overlay from '../Overlay';
import InlineMessage from '../InlineMessage';
import useMessage from '../../lib/useMessage';
import { getCompanyName } from '../../lib/tenant';
import { money } from '../../lib/purchaseOrder';
import {
  buildInvoiceDocument, buildRegister, batchRefusal, describeBatch, batchFilename,
  renderDocumentsPdf, renderRegisterPdf, savePdf, logBatchExport,
  DOCUMENT_BATCH_CAP,
} from '../../lib/invoiceDocuments';

// Issue #298, Session 78n9.4. Gate screen 3 of
// docs/gates/Stede_DocumentOutput_Gate_395.html.
//
// One control, one question, two artifacts. Oracle Payables makes you submit
// the Invoice Register and the Print Invoice Report as two separate requests
// from a screen you are not on; Coupa emails you a zip later. Both fall out of
// the same selection here, synchronously.
//
// ONE CONTROL RATHER THAN THE TWO BUTTONS THE DESKTOP MOCK DRAWS, and that is
// a deliberate reading of the gate rather than a slip. Decision D2 is explicit
// -- "One export control per screen, with a layout choice INSIDE it, not two
// buttons and not two menu items" -- and screen 3's own MOBILE mock shows a
// single button on the selection bar. Only the desktop sketch shows two. The
// decision governs the sketch, and the two agree everywhere else.
//
// Send to QuickBooks stays its own action, because it is a different verb
// rather than a third layout: it writes to somebody else's system and cannot
// be undone by closing a file.

export default function BatchDocumentsSheet({
  isMobile, onClose, onExported,
  // The selected invoice rows, each paired with the order it belongs to.
  // Passed in rather than re-queried: the page already holds both, and a
  // second query is a second opinion about what was selected.
  selection, settings, branchNameFor, profile, scopeLabel, search,
}) {
  const [layout, setLayout] = useState('documents');
  const [working, setWorking] = useState(false);
  const msg = useMessage();

  const count = selection.length;

  // Built up front rather than at export time, so the sheet can state the net
  // total and the credit split BEFORE the file is made. Gate callout E's rule
  // from screen 1, and its reasoning carries over exactly: a figure printed on
  // the file afterwards agrees with whatever the file contains, which is what
  // made screen 1's truncation invisible.
  const documents = selection.map(({ invoice, request }) => buildInvoiceDocument({
    invoice, request, settings, branchName: branchNameFor(request?.branch_id),
  }));
  const register = buildRegister(documents);
  const refusal = batchRefusal(layout, count);

  async function doExport() {
    setWorking(true);
    msg.clearMessage();
    try {
      const generatedAt = new Date();
      const describe = describeBatch({
        companyName: getCompanyName(),
        layout,
        scopeLabel,
        search,
        documentCount: count,
        grand: register.grand,
        generatedBy: profile?.full_name || 'Unknown',
        generatedAt: generatedAt.toLocaleString('en-US'),
      });
      const meta = { companyName: getCompanyName(), scopeLabel, describe };

      const doc = layout === 'register'
        ? await renderRegisterPdf(register, meta)
        : await renderDocumentsPdf(documents, meta);

      savePdf(doc, batchFilename(layout, generatedAt));

      // D9. AFTER the file is produced, so an export that threw is not recorded
      // as one that happened.
      await logBatchExport({
        profile,
        layout,
        filters: { scope: scopeLabel, search: search || null, invoice_ids: selection.map(s => s.invoice?.id) },
        documentCount: count,
      });

      onExported?.(layout, count);
      onClose();
    } catch (e) {
      msg.showError('The documents could not be produced: ' + (e.message || e));
      setWorking(false);
    }
  }

  const pill = (active) => ({
    flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
    border: active ? '1.5px solid #B31F7C' : '1px solid var(--gray-ln, #E3E3DF)',
    background: active ? '#FBEDF5' : '#fff',
    fontWeight: active ? 700 : 500,
    color: 'var(--text-primary)',
  });

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={working ? undefined : onClose} maxWidth={520}>
      <div className="modal-header">
        <span className="modal-title">Export {count} {count === 1 ? 'document' : 'documents'}</span>
        <button className="close-btn" onClick={onClose} disabled={working}>×</button>
      </div>

      <div className="modal-body">
        <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} />

        <div className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          {scopeLabel}{search ? ` · matching “${search}”` : ''}
        </div>

        {/* The question is what you NEED, not which report exists. Screen 1's
            callout D, and it applies harder here: Oracle's two artifacts have
            names — Invoice Register, Print Invoice Report — that you have to
            know before you can find them. */}
        <div className="form-label" style={{ marginBottom: 6 }}>What do you need?</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <button style={pill(layout === 'documents')} onClick={() => setLayout('documents')}>
            Documents
          </button>
          <button style={pill(layout === 'register')} onClick={() => setLayout('register')}>
            Register
          </button>
        </div>
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.45 }}>
          {layout === 'documents'
            ? 'One page per invoice, merged into a single PDF. Each page shows what was ordered, what was received and what was invoiced as three separate figures.'
            : 'One page in total. One row per invoice, grouped by vendor with a subtotal for each — the listing, not the paperwork.'}
        </div>

        {/* D5, and the number is stated BEFORE the file is made. A refusal that
            says only "too many" invites guessing how much smaller smaller is. */}
        <div style={{
          padding: '11px 13px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
          background: refusal ? '#FDF3F3' : 'var(--off-white, #F5F5F3)',
          border: `1px solid ${refusal ? '#F0CFCF' : 'var(--gray-ln, #E3E3DF)'}`,
          marginBottom: 14,
        }}>
          {refusal || (
            <>
              <strong>{count}</strong> {count === 1 ? 'document' : 'documents'} selected
              {layout === 'documents'
                ? `, and all ${count === 1 ? 'of it' : count + ' of them'} will be in the file — one page each.`
                : ', on one page, grouped into ' + register.vendors.length
                  + (register.vendors.length === 1 ? ' vendor.' : ' vendors.')}
            </>
          )}
        </div>

        {/* The net, and what it is made of. A single figure derived from
            invoices minus credits is not self-explanatory, and a reader needs to
            know the credits were SUBTRACTED rather than dropped. Credits are
            stored positive per #299, so this is the one place the sign is
            applied — the same helper the file itself uses. */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: 12,
          padding: '9px 13px', fontSize: 13,
          border: '1px solid var(--gray-ln, #E3E3DF)', borderRadius: 8,
        }}>
          <span>Net total on this selection</span>
          <span style={{ fontWeight: 700 }}>{money(register.grand)}</span>
        </div>
        {register.creditCount > 0 && (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
            {register.invoiceCount} {register.invoiceCount === 1 ? 'invoice' : 'invoices'} less{' '}
            {register.creditCount} vendor {register.creditCount === 1 ? 'credit' : 'credits'}.
            A credit reduces what D&amp;R owes, so it is subtracted here and on the file.
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose} disabled={working}>Cancel</button>
        {/* Blocked rather than refused after the click — an error banner renders
            above the control that triggered it and reads as a dead button. */}
        <button
          className="btn btn-primary"
          onClick={doExport}
          disabled={working || count === 0 || !!refusal}
        >
          {working
            ? (layout === 'documents' && count > DOCUMENT_BATCH_CAP / 3 ? 'Drawing pages…' : 'Exporting…')
            : (layout === 'register' ? 'Export register' : 'Export documents')}
        </button>
      </div>
    </Overlay>
  );
}
